import type { StorageManager } from '@/storage/save-data';
import type { LevelData } from '@/game/level';
import type { SyncManager } from './sync-manager';
import { withAuthBoundary } from './auth-boundary';
import { toast } from '@/ui/toast';
import { resolveConflictWithDialog } from './conflict-dialog';

export type SyncAction = 'push' | 'pull' | 'publish' | 'unpublish' | 'resolve' | 'clear-link' | 'delete-remote';

export async function performSyncAction(
  action: SyncAction,
  level: LevelData,
  deps: { storage: StorageManager; syncManager: SyncManager },
): Promise<void> {
  const { storage, syncManager } = deps;
  try {
    if (action === 'push') {
      const updated = await withAuthBoundary(() => syncManager.pushLevel(level));
      storage.upsertUserLevel(updated);
      toast('Pushed', { variant: 'success' });
    } else if (action === 'pull') {
      const updated = await withAuthBoundary(() => syncManager.pullLevel(level));
      storage.upsertUserLevel(updated);
      toast('Pulled', { variant: 'success' });
    } else if (action === 'publish' || action === 'unpublish') {
      await withAuthBoundary(() => syncManager.publishLevel(level, action === 'publish'));
      toast(action === 'publish' ? 'Published' : 'Unpublished', { variant: 'success' });
    } else if (action === 'resolve') {
      const result = await withAuthBoundary(() => resolveConflictWithDialog(syncManager, level));
      if (result) {
        storage.upsertUserLevel(result.kept);
        if (result.duplicate) storage.upsertUserLevel(result.duplicate);
        toast('Conflict resolved', { variant: 'success' });
      }
    } else if (action === 'clear-link') {
      const cleared = { ...level, meta: { ...level.meta,
        remoteId: undefined, remoteVersion: undefined, remoteDirty: undefined } };
      storage.upsertUserLevel(cleared);
      toast('Cleared remote link', { variant: 'info' });
    } else if (action === 'delete-remote') {
      await withAuthBoundary(() => syncManager.deleteRemoteLevel(level));
      const cleared = { ...level, meta: { ...level.meta,
        remoteId: undefined, remoteVersion: undefined, remoteDirty: undefined } };
      storage.upsertUserLevel(cleared);
      toast('Deleted from server', { variant: 'success' });
    }
  } catch (e: unknown) {
    const err = e as { name?: string; retryAfterSec?: number; field?: string };
    if (err.name === 'NotAuthenticatedError') return;
    toast(mapSyncError(err), { variant: 'error', durationMs: 4000 });
  }
}

/**
 * Pull a server-owned level that has no local copy yet. Used when Level Select
 * discovers a level via `listMineLevels()` on a fresh device.
 */
export async function pullRemoteOnlyLevel(
  remoteId: string,
  deps: { storage: StorageManager; syncManager: SyncManager },
): Promise<void> {
  const { storage, syncManager } = deps;
  try {
    const pulled = await withAuthBoundary(() => syncManager.pullByRemoteId(remoteId));
    storage.upsertUserLevel(pulled);
    toast(`Pulled '${pulled.meta.name}'`, { variant: 'success' });
  } catch (e: unknown) {
    const err = e as { name?: string; retryAfterSec?: number; field?: string };
    if (err.name === 'NotAuthenticatedError') return;
    toast(mapSyncError(err), { variant: 'error', durationMs: 4000 });
  }
}

/**
 * Download a public level to the user's local "Downloaded Levels" list.
 * Used from BrowseScreen's preview modal and from Downloaded tab's re-pull flow.
 */
export async function downloadLevel(
  remoteId: string,
  deps: { storage: StorageManager; syncManager: SyncManager },
): Promise<void> {
  const { storage, syncManager } = deps;
  try {
    const pulled = await withAuthBoundary(() => syncManager.downloadLevel(remoteId));
    storage.upsertDownloadedLevel(pulled);
    toast(`Downloaded '${pulled.meta.name}'`, { variant: 'success' });
  } catch (e: unknown) {
    const err = e as { name?: string; retryAfterSec?: number; field?: string };
    if (err.name === 'NotAuthenticatedError') return;
    toast(mapSyncError(err), { variant: 'error', durationMs: 4000 });
  }
}

/**
 * Server-side remix: creates a new owned level under the current account,
 * preserving original meta.author and setting remixOf lineage. Saved to userLevels.
 */
export async function remixLevel(
  sourceRemoteId: string,
  deps: { storage: StorageManager; syncManager: SyncManager },
): Promise<void> {
  const { storage, syncManager } = deps;
  try {
    const remixed = await withAuthBoundary(() => syncManager.remixLevel(sourceRemoteId));
    storage.upsertUserLevel(remixed);
    toast(`Remixed '${remixed.meta.name}' — saved to My Levels`, { variant: 'success' });
  } catch (e: unknown) {
    const err = e as { name?: string; retryAfterSec?: number; field?: string };
    if (err.name === 'NotAuthenticatedError') return;
    toast(mapSyncError(err), { variant: 'error', durationMs: 4000 });
  }
}

/**
 * Pull the latest server version of an already-downloaded level,
 * overwriting the local copy.
 */
export async function updateDownloadedLevel(
  downloaded: LevelData,
  deps: { storage: StorageManager; syncManager: SyncManager },
): Promise<void> {
  const { storage, syncManager } = deps;
  if (!downloaded.meta.remoteId) return;
  try {
    const pulled = await withAuthBoundary(() => syncManager.pullByRemoteId(downloaded.meta.remoteId!));
    storage.upsertDownloadedLevel(pulled);
    toast('Updated', { variant: 'success' });
  } catch (e: unknown) {
    const err = e as { name?: string; retryAfterSec?: number; field?: string };
    if (err.name === 'NotAuthenticatedError') return;
    toast(mapSyncError(err), { variant: 'error', durationMs: 4000 });
  }
}

/**
 * Remove a downloaded level from local storage. Server copy is untouched.
 * Synchronous — no network call.
 */
export function unlinkDownloadedLevel(
  remoteId: string,
  deps: { storage: StorageManager },
): void {
  deps.storage.removeDownloadedLevel(remoteId);
  toast('Unlinked from local', { variant: 'info' });
}

function mapSyncError(e: { name?: string; retryAfterSec?: number; field?: string }): string {
  if (e.name === 'ConflictError') return "Someone else updated this level — tap RESOLVE.";
  if (e.name === 'RateLimitedError') {
    const s = typeof e.retryAfterSec === 'number' ? ` (retry in ${e.retryAfterSec}s)` : '';
    return `Slow down — too many requests${s}.`;
  }
  if (e.name === 'LimitExceededError') {
    if (e.field === 'size_bytes') return 'This level is too big (256 KB max). Try removing some obstacles.';
    if (e.field === 'levels_per_account') return 'You have too many levels. Delete some to make room.';
    if (e.field === 'meta.tags') return "Some tags aren't on the list — fix them in the editor before publishing.";
    return `Limit exceeded on ${e.field ?? 'unknown'}.`;
  }
  return "Couldn't reach the server. Check your connection and try again.";
}

import * as api from './api-client';

export interface CompletionResult {
  count: number;
  isVerifier: boolean;
  firstCompletedAt: number;
}

export async function submitCompletion(
  remoteId: string,
  levelVersion: number,
  ctx: { syncManager: SyncManager },
): Promise<CompletionResult | null> {
  const session = ctx.syncManager.getSession();
  if (!session) return null;
  try {
    return await api.submitCompletion(session.token, remoteId, { levelVersion });
  } catch (e) {
    console.warn('[completion] submission failed:', e);
    return null;
  }
}
